import { ReactNode, useCallback, useEffect, useState } from "react";
import { useAccordion } from "../hooks";
import { AccordionItemContext } from "./accordion-item.context";

export type AccordionItemProviderProps = {
  defaultOpen: boolean;
  children: ReactNode;
  name: string;
};

type AccordionEvent = {
  name: string;
  isSingle: boolean;
};

export function AccordionItemProvider({
  defaultOpen,
  children,
  name,
}: AccordionItemProviderProps) {
  const { isSingle, dispatchEvent, addEventListener, removeEventListener } =
    useAccordion();
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  const handleOpen = useCallback(() => {
    dispatchEvent("open", { name, isSingle });
  }, [dispatchEvent, isSingle, name]);

  const handleClose = useCallback(() => {
    dispatchEvent("close", { name, isSingle });
  }, [dispatchEvent, isSingle, name]);

  useEffect(() => {
    function onOpen(event: AccordionEvent) {
      if (event.name === name) {
        return setIsOpen(true);
      }

      if (event.isSingle) {
        return setIsOpen(false);
      }
    }

    function onClose(event: AccordionEvent) {
      if (event.name === name) {
        setIsOpen(false);
      }
    }

    addEventListener("close", onClose);
    addEventListener("open", onOpen);

    return () => {
      removeEventListener("close", onClose);
      removeEventListener("open", onOpen);
    };
  }, []);

  return (
    <AccordionItemContext.Provider
      value={{
        handleClose,
        handleOpen,
        isOpen,
        name,
        openedByDefault: defaultOpen,
      }}
    >
      {children}
    </AccordionItemContext.Provider>
  );
}
